import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonDynamicTyping = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Advanced Mechanics</div>
                <h1>The Magic of Dynamic Typing</h1>
                <p className="hero-lead">
                    Python is famous for its flexibility. But how does it know that <code>x = 5</code> is an integer without you saying so?
                    Discover the "Costume Party" of the Python object model.
                </p>
            </div>

            <div className="content-section chapter-intro">
                <p>
                    In "Static" languages like C++ or Java, variables are strict. If you declare a box as "Integer Only", you cannot
                    put a string in it. The compiler checks everything <em>before</em> the program runs.
                </p>
                <p>
                    Python is "Dynamic". It checks types <em>while</em> the program runs. This allows for incredible flexibility,
                    rapid prototyping, and generic code that works on any object... as long as it behaves correctly.
                </p>
                <p>
                    <strong>The Costume Party Analogy:</strong> Imagine a static language is a secure government building. To enter,
                    you need a specific ID Badge (Type Declaration). If you don't have the "Integer" badge, security kicks you out immediately.
                    <br /><br />
                    Python is a Costume Party. The variable <code>x</code> is just a person. Right now, they are wearing a Firefighter costume (Integer).
                    Later, they can go backstage and change into a Clown costume (String). The party (program) doesn't care <em>who</em> they are,
                    only <em>what they can do</em> (Methods) at any given moment.
                </p>

                <div className="learning-objectives">
                    <h3>What You'll Learn</h3>
                    <ul>
                        <li><strong>Strong vs Weak Typing:</strong> Why Python is Dynamic but Strict (unlike JavaScript).</li>
                        <li><strong>Duck Typing:</strong> The "Behavior over Identity" philosophy.</li>
                        <li><strong>Type Hints:</strong> Bringing sanity back to large projects (PEP 484).</li>
                        <li><strong>Runtime Costs:</strong> The performance price we pay for magic.</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Dynamic but Strong</h2>
                <p>
                    There is a dangerous misconception that "Dynamic" means "Anything Goes". That is false. Python is <strong>Strongly Typed</strong>.
                    This means that even though variables can change types, the <em>objects</em> themselves enforce strict rules.
                </p>

                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Feature</th>
                                <th>JavaScript (Dynamic + Weak)</th>
                                <th>Python (Dynamic + Strong)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>Code</strong></td>
                                <td><code>"5" + 5</code></td>
                                <td><code>"5" + 5</code></td>
                            </tr>
                            <tr>
                                <td><strong>Result</strong></td>
                                <td><code>"55"</code> (String)</td>
                                <td><code>TypeError</code> (Crash)</td>
                            </tr>
                            <tr>
                                <td><strong>Why?</strong></td>
                                <td>Implicitly converts number to string to keep going.</td>
                                <td>Refuses to mix incompatible types. Safety first!</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Python's Safety Catch</span>
                    </div>
                    <CodeBlock language="python" code={`x = "10"
y = 5

# This will crash your program (Good!)
# print(x + y)  # TypeError: can only concatenate str (not "int") to str

# You must be explicit
print(int(x) + y)  # 15
print(x + str(y))  # "105"`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Duck Typing: Behavior Over Identity</h2>
                <p>
                    <em>"If it walks like a duck and quacks like a duck, then it must be a duck."</em>
                </p>
                <p>
                    In Python, we rarely check <code>if type(x) == list</code>. Instead, we check if <code>x</code> <em>behaves</em> like a list.
                    Does it have a length? Can we iterate over it? This allows us to write "Generic" functions that work on data types
                    that haven't even been invented yet!
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>The Power of Duck Typing</span>
                    </div>
                    <CodeBlock language="python" code={`class Book:
    def __len__(self):
        return 300  # Pages

class Movie:
    def __len__(self):
        return 120  # Minutes

# This function assumes 'item' is anything with a length
def describe_length(item):
    print(f"This item has length: {len(item)}")

# It works on built-ins
describe_length("Hello")      # This item has length: 5
describe_length([1, 2, 3])    # This item has length: 3

# And on our custom objects!
describe_length(Book())       # This item has length: 300
describe_length(Movie())      # This item has length: 120
    
# We didn't have to inherit from a "Lengthable" interface (like Java)`} />
                </div>
            </div>

            <div className="content-section">
                <h2>The Modern Era: Type Hints (PEP 484)</h2>
                <p>
                    Dynamic typing is great for scripts, but what happens when your codebase grows to 100,000 lines?
                    You look at a function <code>def process(data):</code> and have no idea what <code>data</code> is supposed to be.
                </p>
                <p>
                    Python 3.5 introduced <strong>Type Hints</strong>. These are completely ignored by Python at runtime (so no performance hit),
                    but they are used by IDEs (like VS Code) and static analyzers (like Mypy) to bug-check your code <em>before</em> you run it.
                    It's the best of both worlds: Dynamic runtime, Static analysis.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Modern Python Syntax</span>
                    </div>
                    <CodeBlock language="python" code={`from typing import List, Optional

# Without hints: What is 'items'? List of ints? Strings?
def tough_old_way(items):
    return items[0].upper()

# With hints: We know 'users' is a list of strings
def modern_way(users: List[str]) -&gt; str:
    if not users:
        return ""
    return users[0].upper()

# If you try this in VS Code, it will underline the error!
# modern_way([1, 2, 3])  # Error: List[int] is not compatible with List[str]`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Under the Hood: `__dict__`</h2>
                <p>
                    How does Python actually store all these dynamic attributes? Almost every object in Python is secretly just a dictionary
                    wrapper! When you say <code>obj.x = 5</code>, Python is really doing <code>obj.__dict__['x'] = 5</code>.
                </p>
                <div className="code-example">
                    <div className="code-header">
                        <span>The Secret Dictionary</span>
                    </div>
                    <CodeBlock language="python" code={`class Hero:
    pass

p = Hero()
p.name = "Batman"
p.power = 9000

print(p.__dict__)
# Output: {'name': 'Batman', 'power': 9000}

# This explains why we can add attributes at runtime!
p.sidekick = "Robin"
print(p.__dict__)
# Output: {'name': 'Batman', 'power': 9000, 'sidekick': 'Robin'}`} />
                </div>
                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Performance Note:</strong> Because every attribute access is a hash map lookup, Python is slower than C.
                        If you need speed and memory efficiency (e.g., for millions of objects), you can use <code>__slots__</code> to
                        disable the dynamic <code>__dict__</code> behavior.
                    </div></ProTip>
            </div>

            <div className="content-section key-takeaways">
                <h2> Key Takeaways</h2>
                <div className="takeaway-grid">
                    <div className="takeaway-item">
                        <h3>Dynamic != Weak</h3>
                        <p>Python is Dynamic (types change) but Strong (types don't mix). It catches type errors at runtime rather than silently converting data.</p>
                    </div>
                    <div className="takeaway-item">
                        <h3>Duck Typing</h3>
                        <p>Write generic functions that use methods (like <code>.read()</code> or <code>len()</code>) rather than checking types (<code>isinstance</code>).</p>
                    </div>
                    <div className="takeaway-item">
                        <h3>Type Hints</h3>
                        <p>Use Type Hints (<code>: int</code>) for documentation and IDE support. They don't affect runtime but make maintenance infinity easier.</p>
                    </div>
                    <div className="takeaway-item">
                        <h3>Runtime Cost</h3>
                        <p>Dynamic lookup is slower than static memory access. Use this flexibility wisely.</p>
                    </div>
                </div>
            </div>

            <div className="external-resources">
                <h3> Further Reading</h3>
                <div className="resource-links">
                    <a href="https://peps.python.org/pep-0484/" target="_blank" rel="noopener noreferrer">
                        PEP 484: Type Hints
                    </a>
                    <a href="https://realpython.com/python-type-checking/" target="_blank" rel="noopener noreferrer">
                        RealPython: Static Type Checking
                    </a>
                    <a href="https://mypy.readthedocs.io/en/stable/" target="_blank" rel="noopener noreferrer">
                        Mypy Documentation
                    </a>
                </div>
            </div>
        </div>
    );
};

export default PythonDynamicTyping;


